<template>
  <side-bar>
    <menu-group v-if="freeEvaluationIds.length > 0" class="pt-4 pb-4">
      <h3 class="label mt-1 mb-2">{{ $t("headings.savedEvaluations") }}</h3>
      <button-list
        :items="freeEvaluations"
        :order="freeEvaluationIds"
        :selected-item="currentEvaluation"
        :deletable="true"
        @select="openTab($event)"
        @delete="deleteEvaluation($event.id)"
        @contextmenu="openContextMenu($event, $event.item?.id)"
      >
        <template #default="{ item: evaluation }">
          <div class="line">
            <span
              style="
                min-width: 1rem;
                text-align: center;
                color: var(--color-text-medium);
              "
            >
              <lucide-icon
                v-if="evaluation.chartType != null"
                :name="charts?.[evaluation.chartType]?.icon"
                class="self-start"
                style="margin-top: 1px"
              />
              <span v-else>?</span>
            </span>
            <span>{{ evaluation.title || $t("labels.unnamed") }}</span>
          </div>
        </template>

        <template #after-list>
          <div class="mt-2">
            <button class="button" type="button" @click="createEvaluation()">
              {{ $t("actions.createEvaluation") }}
            </button>
          </div>
        </template>
      </button-list>
    </menu-group>

    <context-menu
      v-if="contextmenu.evaluationId != null"
      :x="contextmenu.x"
      :y="contextmenu.y"
      @close="contextmenu.evaluationId = null"
    >
      <button
        type="button"
        class="context-menu__entry"
        @click="deleteEvaluation(contextmenu.evaluationId)"
      >
        {{ $t("actions.delete") }}
      </button>
    </context-menu>

    <menu-group>
      <h4 class="label">{{ $t("headings.savedEvaluationOverviews") }}</h4>

      <button-list
        :items="evaluationOverviews"
        :order="evaluationOverviewsOrder"
        :deletable="true"
        :selected-item="currentEvaluationOverview"
        class="mb-4 mt-2"
        @select="openTab($event)"
        @delete="deleteEvaluationOverview($event.id)"
      >
        <template #default="{ item: overview }">
          <div>
            <evaluation-overview-layout-symbol :overview-id="overview.id" />
            {{ overview.title || overview.id + 1 }}
          </div>
          <div
            style="
              margin-top: 0.25rem;
              color: var(--color-text-light);
              margin-left: 1.75rem;
            "
          >
            {{ $d(new Date(overview.createdAt)) }}
          </div>
        </template>

        <template #after-list>
          <button
            class="button mt-2"
            type="button"
            @click="createEvaluationOverview()"
          >
            {{ $t("labels.newEvaluationOverview") }}
          </button>
        </template>
      </button-list>
    </menu-group>
  </side-bar>
</template>

<script setup lang="ts">
import { reactive, computed } from "vue";

import SideBar from "@core/ui/SideBar.vue";
import MenuGroup from "@core/ui/MenuGroup.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import ButtonList from "@core/ui/ButtonList.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";

import EvaluationOverviewLayoutSymbol from "@editor/items/evaluation/EvaluationOverviewLayoutSymbol.vue";

import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";
import { useEvaluationViewStore } from "./evaluationView.store";
import { useEvaluationOverviewStore } from "@editor/items/evaluation/evaluationOverviews.store";

import { pickBy } from "@/helpers/utility/CollectionFunctions";
import { charts } from "@editor/items/charts/charts";

const evaluationViewStore = useEvaluationViewStore();

const { openTab } = evaluationViewStore;

// ==================
// Evaluations

const currentEvaluation = computed(() => evaluationViewStore.currentEvaluation);

const evaluations = computed(() => {
  return useEvaluationStore().items;
});
const freeEvaluations = computed(() => {
  return pickBy(
    useEvaluationStore().items,
    (item) => !item.isDefaultSimulation
  );
});
const freeEvaluationIds = computed((): number[] => {
  return Object.keys(freeEvaluations.value).map((id) => parseInt(id, 10));
});

async function createEvaluation() {
  const evaluation = await useEvaluationStore().create();
  openTab(evaluation);
}

function deleteEvaluation(evaluationId: number | null) {
  if (evaluationId == null) return;
  const evaluation = evaluations.value[evaluationId];
  if (evaluation == null) return;
  if (evaluation.isDefaultSimulation) return;
  useEvaluationStore().delete({ id: evaluation?.id });
}

// ==================
// Evaluation Overviews

const evaluationOverviews = computed(() => {
  return useEvaluationOverviewStore().items;
});
const evaluationOverviewsOrder = computed(() => {
  return useEvaluationOverviewStore().order;
});
const currentEvaluationOverview = computed(
  () => evaluationViewStore.currentEvaluationOverview
);

async function createEvaluationOverview() {
  const overview = await useEvaluationOverviewStore().create();
  openTab(overview);
}

function deleteEvaluationOverview(evaluationOverviewId: number) {
  useEvaluationViewStore().closeTab({
    type: "evaluationOverview",
    id: evaluationOverviewId
  });
  return useEvaluationOverviewStore().delete({ id: evaluationOverviewId });
}

// ==================
// Context Menu
// ------------------

const contextmenu = reactive({
  evaluationId: null as null | number,
  x: 0,
  y: 0
});

function openContextMenu(event: MouseEvent, evaluationId: number | undefined) {
  if (evaluationId == null) {
    contextmenu.evaluationId = null;
    return;
  }
  contextmenu.evaluationId = evaluationId;
  contextmenu.x = event.clientX;
  contextmenu.y = event.clientY;
}
</script>
